// tienda/HeroSection.jsx
// Stripe-inspired hero adapted to a Lima logistics command center.

import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Clock,
  HandCoins,
  MessageSquare,
  PackageCheck,
  Route,
  ShieldCheck,
  Truck,
  Zap,
} from "lucide-react";
import { brand, WA_HREF, fadeUp } from "../shared/brand";
import { MapMockup } from "./MapMockup";

const ORDERS = [
  { code: "María Flores", zone: "Miraflores", amount: "Coordinado", state: "COD listo" },
  { code: "Juan Pérez", zone: "Surco", amount: "Clásico", state: "Recojo" },
  { code: "FM-0243", zone: "San Isidro", amount: "Premium", state: "Prioridad" },
];

const SIGNALS = [
  { icon: <Clock className="size-4" />, label: "Recojo", value: "hasta 12 PM" },
  { icon: <HandCoins className="size-4" />, label: "Same-day", value: "hasta 2 PM" },
  { icon: <MessageSquare className="size-4" />, label: "Recomendado", value: "Coordinado" },
];

export function HeroSection() {
  return (
    <section className="relative isolate overflow-hidden border-b" style={{ background: brand.bg, borderColor: brand.line }}>
      <div
        className="absolute inset-0 -z-10"
        style={{
          background:
            `radial-gradient(circle at 15% 15%, ${brand.accentGold}33, transparent 28%),` +
            `radial-gradient(circle at 82% 8%, ${brand.accentGreen}2E, transparent 30%),` +
            `linear-gradient(135deg, ${brand.bg} 0%, #FFFFFF 44%, #F7EEF4 100%)`,
        }}
      />
      <div
        className="absolute inset-0 -z-10 opacity-[0.45]"
        style={{
          backgroundImage:
            `linear-gradient(${brand.line} 1px, transparent 1px), linear-gradient(90deg, ${brand.line} 1px, transparent 1px)`,
          backgroundSize: "56px 56px",
          maskImage: "linear-gradient(to bottom, black, transparent 82%)",
        }}
      />

      <div className="mx-auto grid max-w-7xl gap-10 px-4 pb-14 pt-12 md:grid-cols-[0.9fr_1.1fr] md:pb-20 md:pt-16 lg:gap-14">
        <motion.div {...fadeUp} className="flex flex-col justify-center">
          <div
            className="mb-5 inline-flex w-fit items-center gap-2 rounded-full border px-3 py-1 text-xs font-bold"
            style={{ background: "#FFFFFFCC", borderColor: brand.line, color: brand.plum }}
          >
            <Zap className="size-3.5" style={{ color: brand.accentGold }} />
            Clásico · Coordinado · Premium para tiendas online
          </div>

          <h1 className="max-w-3xl text-4xl font-black leading-[1.02] md:text-6xl" style={{ color: brand.ink }}>
            Delivery para ecommerce con 3 niveles de éxito operativo.
          </h1>

          <p className="mt-5 max-w-xl text-base leading-relaxed md:text-lg" style={{ color: brand.muted }}>
            Elige Clásico para entregar, Coordinado para reducir fricción y aumentar entregas efectivas,
            o Premium para priorizar pedidos urgentes dentro de la ruta.
          </p>

          <div className="mt-7 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 text-sm font-bold text-white shadow-lg shadow-pink-900/10 transition-transform hover:-translate-y-0.5"
              style={{ background: brand.primary }}
            >
              Crear cuenta gratis
              <ArrowRight className="size-4" />
            </Link>
            <a
              href={WA_HREF}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-xl border bg-white/80 px-5 py-3 text-sm font-bold transition-colors hover:bg-white"
              style={{ borderColor: brand.line, color: brand.ink }}
            >
              Evaluar mi operación
            </a>
          </div>

          <div className="mt-8 grid max-w-xl grid-cols-3 gap-3">
            {SIGNALS.map(item => (
              <div key={item.label} className="rounded-2xl border bg-white/75 p-3 shadow-sm" style={{ borderColor: brand.line }}>
                <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold" style={{ color: brand.muted }}>
                  <span style={{ color: brand.primary }}>{item.icon}</span>
                  {item.label}
                </div>
                <div className="text-sm font-black" style={{ color: brand.ink }}>{item.value}</div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.08 }} className="relative">
          <OperationsCockpit />
        </motion.div>
      </div>
    </section>
  );
}

function OperationsCockpit() {
  return (
    <div className="relative mx-auto max-w-2xl">
      <div className="absolute -inset-4 rounded-[2rem] bg-white/45 blur-2xl" />
      <div className="relative overflow-hidden rounded-[1.75rem] border bg-white shadow-2xl shadow-pink-950/10" style={{ borderColor: brand.line }}>
        <div className="flex items-center justify-between border-b px-4 py-3" style={{ borderColor: brand.line }}>
          <div className="flex items-center gap-2">
            <span className="grid size-8 place-items-center rounded-xl text-white" style={{ background: brand.primary }}>
              <Route className="size-4" />
            </span>
            <div>
              <p className="text-sm font-black" style={{ color: brand.ink }}>Centro de entregas</p>
              <p className="text-[11px]" style={{ color: brand.muted }}>Lima Metropolitana · hoy</p>
            </div>
          </div>
          <div className="rounded-full px-3 py-1 text-[11px] font-bold text-white" style={{ background: brand.accentGreen }}>
            18 en ruta
          </div>
        </div>

        <div className="grid md:grid-cols-5">
          <div className="space-y-3 border-b p-4 md:col-span-2 md:border-b-0 md:border-r" style={{ borderColor: brand.line }}>
            <div className="rounded-2xl p-4 text-white" style={{ background: `linear-gradient(135deg, ${brand.primary}, ${brand.plum})` }}>
              <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-white/75">Corte recojo tienda</span>
                <Clock className="size-4 text-white/75" />
              </div>
              <div className="mt-2 text-3xl font-black">12:00</div>
              <p className="mt-1 text-xs text-white/75">Entregas same-day hasta 2:00 PM o hasta que llegue el driver al recojo.</p>
            </div>

            {ORDERS.map((order, index) => (
              <motion.div
                key={order.code}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 + index * 0.12, duration: 0.45 }}
                className="rounded-2xl border bg-white p-3"
                style={{ borderColor: brand.line }}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <span className="grid size-9 place-items-center rounded-xl" style={{ background: `${brand.primary}12`, color: brand.primary }}>
                      <Truck className="size-4" />
                    </span>
                    <div>
                      <p className="text-xs font-black" style={{ color: brand.ink }}>{order.code}</p>
                      <p className="text-[11px]" style={{ color: brand.muted }}>{order.zone}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-black" style={{ color: brand.ink }}>{order.amount}</p>
                    <p className="text-[11px] font-bold" style={{ color: index === 1 ? brand.accentGold : brand.accentGreen }}>
                      {order.state}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="relative min-h-[360px] md:col-span-3">
            <MapMockup />
            <motion.div
              animate={{ y: [0, -5, 0] }}
              transition={{ duration: 2.8, repeat: Infinity, ease: "easeInOut" }}
              className="absolute right-4 top-4 w-44 rounded-2xl border bg-white/95 p-3 shadow-xl"
              style={{ borderColor: brand.line }}
            >
              <div className="mb-2 flex items-center gap-2">
                <MessageSquare className="size-4" style={{ color: brand.accentGreen }} />
                <p className="text-xs font-black" style={{ color: brand.ink }}>WhatsApp enviado</p>
              </div>
              <p className="text-[11px] leading-relaxed" style={{ color: brand.muted }}>
                Hola María, tu entrega Coordinado está en ruta. Puedes ver el resumen y ubicación.
              </p>
            </motion.div>
            <div className="absolute bottom-4 left-4 grid gap-2">
              <MiniStatus icon={<PackageCheck className="size-4" />} label="Despachados" value="42" color={brand.primary} />
              <MiniStatus icon={<ShieldCheck className="size-4" />} label="A tiempo" value="95%" color={brand.accentGreen} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniStatus({ icon, label, value, color }) {
  return (
    <div className="flex items-center gap-2 rounded-2xl border bg-white/95 px-3 py-2 shadow-lg" style={{ borderColor: brand.line }}>
      <span className="grid size-8 place-items-center rounded-xl" style={{ background: `${color}15`, color }}>
        {icon}
      </span>
      <div>
        <p className="text-[10px] font-semibold" style={{ color: brand.muted }}>{label}</p>
        <p className="text-sm font-black" style={{ color: brand.ink }}>{value}</p>
      </div>
    </div>
  );
}
